import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { getPMDetails } from '~/integrations/PracticeManagement/pm.utils';
import { relationshipModel } from '~/models';
import PMCompanyDropdown from '../PMCompanyDropdown/PMCompanyDropdown';
import { OptionType } from '../PMCompanyDropdown/PMCompanyDropdown.utils';
import styles from './PMSyncCompaniesModal.module.scss';
export default function PMSyncCompaniesModal(param) {
    let { dismissModal, practiceManagementType, relationship, getMatchingCompanies, getLinkCompany, createNewCompanyAndContact, refresh } = param;
    const pmDetails = getPMDetails(practiceManagementType);
    const [companies, setCompanies] = useState([]);
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: practiceManagementTexts } = useLocaleKeys('integrations.practiceManagement');
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loading, setLoading] = useState(false);
    useOnMount(()=>{
        async function fetchCompanies() {
            try {
                setLoading(true);
                const companies = await getMatchingCompanies();
                setCompanies(companies);
            } catch (err) {
                overlayContext.showErrorToast("Failed to load ".concat(pmDetails.company, "s"), 'failed-to-load-companies-toast');
            } finally{
                setLoading(false);
            }
        }
        fetchCompanies();
    });
    const handleCompanyRetrySync = async ()=>{
        if (!selectedCompany) return;
        overlayContext.startLoader();
        if (selectedCompany.type === OptionType.CreateNew) {
            const result = await handleRequest(createNewCompanyAndContact(relationship.id));
            overlayContext.stopLoader();
            if (!result.success) {
                overlayContext.showErrorToast("Sync failed", 'failed-to-create-company-toast');
                return;
            }
            await refresh();
            dismissModal();
            overlayContext.showSuccessToast("".concat(pmDetails.items, " synced to ").concat(pmDetails.name), 'company-created-success-toast');
        } else {
            if (!selectedCompany.company) {
                overlayContext.stopLoader();
                return;
            }
            const result = await handleRequest(getLinkCompany(selectedCompany.company.companyId));
            overlayContext.stopLoader();
            if (!result.success) {
                overlayContext.showErrorToast("Sync failed", 'failed-to-link-company-toast');
                return;
            }
            await refresh();
            dismissModal();
            overlayContext.showSuccessToast("".concat(pmDetails.items, " synced to ").concat(pmDetails.name), 'company-linked-success-toast');
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'p_m_sync_companies_confirm'
            },
            secondaryButton: {
                name: 'p_m_sync_companies_cancel'
            }
        },
        width: "600px",
        testId: "pm-sync-companies-modal",
        title: practiceManagementTexts.selectCompany({
            company: pmDetails.company
        }),
        onCloseButtonClick: dismissModal,
        onPrimaryButtonClick: handleCompanyRetrySync,
        primaryButtonLabel: commonTexts.buttons.retrySyncBtn(),
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onSecondaryButtonClick: dismissModal,
        primaryButtonTestId: "pm-sync-companies-modal-retry-sync-btn",
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.contentContainer,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    align: "left",
                    testId: "pm-sync-companies-modal-description",
                    children: "Select the ".concat(pmDetails.company, " for which you want to automatically generate ").concat(pmDetails.items, " in ").concat(pmDetails.name, " based on the services in your agreement with ").concat(relationshipModel.customerName(relationship), ".")
                }),
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": "pm-company-dropdown",
                    children: /*#__PURE__*/ _jsx(PMCompanyDropdown, {
                        companies: companies,
                        value: selectedCompany,
                        onChange: setSelectedCompany,
                        loading: loading,
                        pmType: practiceManagementType
                    })
                })
            ]
        })
    });
}
